import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Rating,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_URL, INPUT_LIMITS } from '../../consts';
import { useSelector, useDispatch } from 'react-redux';

const ReportTicketDialog = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);

  const [formData, setFormData] = useState({
    type: 'bug',
    subject: '',
    description: '',
    rating: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    // Apply length limits based on field type
    if (field === 'subject') {
      value = value.slice(0, INPUT_LIMITS.NAME);
    } else if (field === 'description') {
      value = value.slice(0, INPUT_LIMITS.TICKET_DESCRIPTION);
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  };

  const handleRatingChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      rating: newValue,
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!formData.description.trim() || formData.description.trim().length < 10) {
      setError('Description must be at least 10 characters');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload = {
        type: formData.type,
        subject: formData.subject.trim(),
        description: formData.description.trim(),
      };

      if (formData.type === 'feedback' && formData.rating) {
        payload.rating = formData.rating;
      }

      await axios.post(`${API_URL}/tickets`, payload, {
        withCredentials: true, // SECURITY: Cookies are sent automatically
      });

      // Reset form
      setFormData({
        type: 'bug',
        subject: '',
        description: '',
        rating: null,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating ticket:', error);
      if (error.response?.status === 401) {
        dispatch({ type: 'SET_UNAUTHORIZED' });
      } else {
        setError(error.response?.data?.error || 'Failed to create ticket. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        type: 'bug',
        subject: '',
        description: '',
        rating: null,
      });
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              How can we help you?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tell us what's on your mind and we'll get back to you as soon as possible.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>What do you need help with?</InputLabel>
            <Select
              value={formData.type}
              label="What do you need help with?"
              onChange={handleChange('type')}
              disabled={loading}
            >
              <MenuItem value="bug">Something isn't working</MenuItem>
              <MenuItem value="user_report">Report a user</MenuItem>
              <MenuItem value="feedback">Share feedback or suggestions</MenuItem>
              <MenuItem value="other">General question</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="What's this about?"
            value={formData.subject}
            onChange={handleChange('subject')}
            required
            disabled={loading}
            sx={{ mb: 3 }}
            placeholder="Give us a brief summary..."
            helperText="A short title helps us understand your request quickly"
            inputProps={{
              maxLength: INPUT_LIMITS.NAME
            }}
          />

          <TextField
            fullWidth
            label="Tell us more"
            value={formData.description}
            onChange={handleChange('description')}
            required
            multiline
            rows={6}
            disabled={loading}
            sx={{ mb: 3 }}
            placeholder="Please share as many details as possible. The more information you provide, the better we can help you..."
            helperText={`${formData.description.length}/${INPUT_LIMITS.TICKET_DESCRIPTION} characters (at least 10 characters needed)`}
            inputProps={{
              maxLength: INPUT_LIMITS.TICKET_DESCRIPTION
            }}
          />

          {formData.type === 'feedback' && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                How would you rate your experience? (Optional)
              </Typography>
              <Rating
                value={formData.rating}
                onChange={handleRatingChange}
                max={5}
                disabled={loading}
                size="large"
              />
            </Box>
          )}

          {error && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography color="error.dark" variant="body2" fontWeight={500}>
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading} size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.subject.trim() || !formData.description.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          size="large"
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
            },
            px: 4,
          }}
        >
          {loading ? 'Sending...' : 'Send Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportTicketDialog;

